/* eslint-disable react/no-unknown-property */
import { useEffect, useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

type AnimatedFlowLineLikeProps = {
  points: THREE.Vector3[];
  color: THREE.ColorRepresentation;
  lineWidth?: number;   // 화살표 머리 너비에 간접 반영
  opacity?: number;     // 머리 투명도
  speed?: number;       // 흐르는 속도 (u/sec 느낌)
  dashSize?: number;    // 머리 길이로 매핑
  dashGap?: number;     // 머리 간 간격에 간접 반영
  frustumCulled?: boolean;
  // 참고 코드와 동일 동작을 위한 추가 옵션
  arrowCount?: number;  // 원하는 화살표 개수 (지정 시 고정 개수)
  headLength?: number;  // 화살표 머리 길이 (지정 시 우선)
  headWidth?: number;   // 화살표 머리 너비 (지정 시 우선)
  depthTest?: boolean;  // true: 지구 뒤편 숨김, false: 항상 보임
  updateEverySec?: number; // 스로틀 간격(초). 0이면 매 프레임 갱신
};

// AnimatedFlowLine과 동일 프롭스 시그니처. 점선 대신 ArrowHelper의 머리만 흐르게 렌더링
export const TriangleDashedFlowLine = ({
  points,
  color,
  lineWidth = 1.5,
  opacity = 1,
  speed = 0.3,
  dashSize = 0.45,
  dashGap = 2,
  frustumCulled = false,
  arrowCount,
  headLength: headLengthProp,
  headWidth: headWidthProp,
  depthTest = true,
  updateEverySec = 0,
}: AnimatedFlowLineLikeProps) => {
  const instRef = useRef<THREE.InstancedMesh>(null);
  const offsetURef = useRef(0);
  // 스로틀 미사용(매 프레임 갱신)

  const curve = useMemo(() => {
    // points 배열이 유효하지 않으면 null 반환
    if (!points || !Array.isArray(points) || points.length < 2) {
      return null;
    }
    
    try {
      return new THREE.CatmullRomCurve3(points, false, 'catmullrom', 0.1);
    } catch (error) {
      console.warn('CatmullRomCurve3 생성 실패:', error);
      return null;
    }
  }, [points]);

  // 곡선 길이에 기반한 화살표 개수 및 간격 계산
  const { count, spacingU, headLength, headWidth } = useMemo(() => {
    if (!curve) {
      return { count: 0, spacingU: 0, headLength: dashSize, headWidth: lineWidth * 0.25 };
    }
    
    try {
      const len = curve.getLength();
      let c: number;
      if (typeof arrowCount === 'number' && arrowCount > 1) {
        c = Math.floor(arrowCount);
      } else {
        // 점선(drei)의 dashSize/dashGap 감각을 유지하며 밀도 자동 계산
        const densityFactor = Math.max(1, dashGap);
        const baseUnit = Math.max(0.0001, dashSize);
        const desired = Math.floor((len / baseUnit) * densityFactor);
        c = Math.max(2, Math.min(2000, desired));
      }
      const uSpacing = 1 / c;
      const computedHeadLength = headLengthProp ?? dashSize;                 // 머리 길이
      const computedHeadWidth = headWidthProp ?? Math.max(0.0001, lineWidth * 0.25); // 너비
      return { count: c, spacingU: uSpacing, headLength: computedHeadLength, headWidth: computedHeadWidth };
    } catch (error) {
      console.warn('곡선 길이 계산 실패:', error);
      return { count: 0, spacingU: 0, headLength: dashSize, headWidth: lineWidth * 0.25 };
    }
  }, [curve, dashSize, dashGap, lineWidth, arrowCount, headLengthProp, headWidthProp]);

  // 곡선 샘플 캐시(위치/접선)로 CPU 부하 완화
  const sampler = useMemo(() => {
    if (!curve) {
      return { 
        sampleCount: 0, 
        get: (u: number, outPos: THREE.Vector3, outTan: THREE.Vector3) => {
          outPos.set(0, 0, 0);
          outTan.set(0, 0, 1);
        }
      };
    }
    
    try {
      const sampleCount = Math.min(4096, Math.max(256, Math.floor(curve.getLength() / Math.max(0.001, (headLengthProp ?? dashSize) * 0.25))));
      const positions = new Float32Array(sampleCount * 3);
      const tangents = new Float32Array(sampleCount * 3);
      const p = new THREE.Vector3();
      const t = new THREE.Vector3();
      for (let i = 0; i < sampleCount; i++) {
        const u = i / (sampleCount - 1);
        curve.getPoint(u, p);
        curve.getTangent(u, t).normalize();
        positions[i * 3 + 0] = p.x;
        positions[i * 3 + 1] = p.y;
        positions[i * 3 + 2] = p.z;
        tangents[i * 3 + 0] = t.x;
        tangents[i * 3 + 1] = t.y;
        tangents[i * 3 + 2] = t.z;
      }
      const get = (u: number, outPos: THREE.Vector3, outTan: THREE.Vector3) => {
        const iu = u * (sampleCount - 1);
        const i0 = Math.floor(iu);
        const i1 = Math.min(sampleCount - 1, i0 + 1);
        const f = iu - i0;
        const a = i0 * 3, b = i1 * 3;
        outPos.set(
          positions[a] * (1 - f) + positions[b] * f,
          positions[a + 1] * (1 - f) + positions[b + 1] * f,
          positions[a + 2] * (1 - f) + positions[b + 2] * f,
        );
        outTan.set(
          tangents[a] * (1 - f) + tangents[b] * f,
          tangents[a + 1] * (1 - f) + tangents[b + 1] * f,
          tangents[a + 2] * (1 - f) + tangents[b + 2] * f,
        ).normalize();
      };
      return { sampleCount, get };
    } catch (error) {
      console.warn('곡선 샘플러 생성 실패:', error);
      return { 
        sampleCount: 0, 
        get: (u: number, outPos: THREE.Vector3, outTan: THREE.Vector3) => {
          outPos.set(0, 0, 0);
          outTan.set(0, 0, 1);
        }
      };
    }
  }, [curve, headLengthProp, dashSize]);

  // cone 지오메트리/머티리얼 + 인스턴스 초기화
  const coneGeo = useMemo(() => new THREE.ConeGeometry(0.5, 1, 12, 1, false), []);
  const coneMat = useMemo(() => new THREE.MeshBasicMaterial({
    color,
    transparent: opacity < 1,
    opacity,
    depthTest,
    depthWrite: depthTest,
  }), [color, opacity, depthTest]);

  useEffect(() => {
    if (!instRef.current) return;
    instRef.current.count = count;
    instRef.current.instanceMatrix.needsUpdate = true;
  }, [count]);

  // 흐름 애니메이션
  useFrame((_, delta) => {
    if (!instRef.current || count === 0) return;
    // speed=1 → 초당 화살표 간격(spacingU)만큼 전진 (항상 매 프레임 갱신)
    const normalizedStep = delta * speed * spacingU;
    offsetURef.current = (offsetURef.current + normalizedStep) % 1;

    const dummy = new THREE.Object3D();
    const pos = new THREE.Vector3();
    const tan = new THREE.Vector3();
    const normal = new THREE.Vector3();
    const xAxis = new THREE.Vector3();
    const yAxis = new THREE.Vector3();
    const zAxis = new THREE.Vector3();
    const basis = new THREE.Matrix4();

    for (let i = 0; i < count; i++) {
      let u = i * spacingU + offsetURef.current;
      u = ((u % 1) + 1) % 1;

      try {
        sampler.get(u, pos, tan);
        // 구면 프레임 정렬: 접선(Y+), 표면 노멀(Z+), X = Y × Z
        yAxis.copy(tan);
        normal.copy(pos).normalize();
        xAxis.crossVectors(yAxis, normal).normalize();
        zAxis.crossVectors(xAxis, yAxis).normalize();
        basis.makeBasis(xAxis, yAxis, zAxis);

        dummy.position.copy(pos);
        dummy.quaternion.setFromRotationMatrix(basis);
        // Cone은 Y+가 길이 방향 → [너비, 길이, 너비]
        dummy.scale.set(headWidth, headLength, headWidth);
        dummy.updateMatrix();
        instRef.current.setMatrixAt(i, dummy.matrix);
      } catch (error) {
        console.warn(`화살표 ${i} 위치 계산 실패:`, error);
        // 기본 위치로 설정
        dummy.position.set(0, 0, 0);
        dummy.quaternion.set(0, 0, 0, 1);
        dummy.scale.set(0, 0, 0);
        dummy.updateMatrix();
        instRef.current.setMatrixAt(i, dummy.matrix);
      }
    }
    instRef.current.instanceMatrix.needsUpdate = true;
  });

  // curve가 null이거나 count가 0이면 빈 컴포넌트 반환
  if (!curve || count === 0) {
    return null;
  }

  return (
    <instancedMesh ref={instRef} args={[coneGeo, coneMat, count]} frustumCulled={frustumCulled} />
  );
};
